import { Helmet } from 'react-helmet'
import Header from '../components/header'
import {
    CardGrid,
    CtaBand,
    CtaLink,
    FeatureCard,
    MarketingHero,
    MarketingInner,
    MarketingIntro,
    MarketingPage,
    MarketingSection,
    SplitBlock,
    StepCard,
    StepGrid,
} from '../components/styles/Marketing.styled'

const BENEFITS = [
    {
        title: 'Personalized comparisons',
        body: 'Review plan options based on your service area and energy needs, not a generic statewide rate table.',
    },
    {
        title: 'Apples-to-apples visibility',
        body: 'Look beyond headline rates and compare the estimated cost of plans more clearly for your home.',
    },
    {
        title: 'Less confusion',
        body: 'Avoid spending hours sorting through plan structures, bill credits, usage thresholds, and fine print.',
    },
    {
        title: 'Guided decisions',
        body: 'Use Texas Energy Group’s advisor-style approach to better understand what you are choosing before you enroll.',
    },
]

const STEPS = [
    'Enter your service address',
    'Review personalized plan options',
    'Choose the plan that fits your need',
    'Enroll online',
]

export const About = () => {
    return (
        <>
            <Helmet>
                <title>About Us - TEXAS ENERGY GROUP</title>
                <meta
                    name="description"
                    content="Texas Energy Group makes residential electricity shopping simpler, more transparent, and aligned with your household’s needs."
                />
            </Helmet>
            <Header />
            <MarketingPage id="main-content">
                <MarketingHero>
                    <MarketingInner>
                        <MarketingIntro>
                            <h1>Ready to Shop Smarter?</h1>
                            <p>
                                Texas Energy Group is built around helping customers navigate the
                                energy landscape with confidence. Whether you are moving, renewing,
                                or comparing plans for the first time, our goal is to make the
                                process simple, transparent, and aligned with your household’s
                                needs.
                            </p>
                        </MarketingIntro>
                    </MarketingInner>
                </MarketingHero>

                <MarketingSection>
                    <MarketingInner>
                        <SplitBlock>
                            <h2>Why homeowners use Texas Energy Group</h2>
                            <div>
                                <p>
                                    Electricity shopping in Texas can be overwhelming. Rates are
                                    often advertised at specific usage levels, plan details can be
                                    hard to compare, and the lowest-looking offer is not always the
                                    lowest-cost option for your home.
                                </p>
                                <p>
                                    Texas Energy Group combines decades of local energy market
                                    experience with advanced AI technology to deliver a simpler,
                                    smarter way to shop.
                                </p>
                            </div>
                        </SplitBlock>
                    </MarketingInner>
                </MarketingSection>

                <MarketingSection $alt>
                    <MarketingInner>
                        <SplitBlock>
                            <h2>House or apartment, shop with confidence</h2>
                            <p>
                                Proudly serving thousands of Texas residential customers. Texas
                                Energy Group makes it easier to compare electricity plans,
                                understand the true cost of service, and enroll in a plan that fits
                                your home size and budget.
                            </p>
                        </SplitBlock>
                        <CardGrid>
                            {BENEFITS.map((item) => (
                                <FeatureCard key={item.title}>
                                    <h3>{item.title}</h3>
                                    <p>{item.body}</p>
                                </FeatureCard>
                            ))}
                        </CardGrid>
                    </MarketingInner>
                </MarketingSection>

                <MarketingSection>
                    <MarketingInner>
                        <SplitBlock>
                            <h2>Powered by advanced technology</h2>
                            <div>
                                <p>
                                    Our residential shopping experience is designed to make
                                    electricity shopping faster, easier, and more transparent.
                                    Instead of relying only on generic rate tables, the platform
                                    supports data-driven comparisons that help customers evaluate
                                    plans based on practical cost considerations and plan details.
                                </p>
                                <p>
                                    That means homeowners or renters can spend less time guessing
                                    and more time choosing a plan with confidence.
                                </p>
                            </div>
                        </SplitBlock>
                    </MarketingInner>
                </MarketingSection>

                <MarketingSection $alt>
                    <MarketingInner>
                        <SplitBlock>
                            <h2>How it works</h2>
                            <p>
                                Four steps from address to enrollment. No meter changes, no
                                paperwork maze — just a clearer path to a plan that fits.
                            </p>
                        </SplitBlock>
                        <StepGrid>
                            {STEPS.map((step, index) => (
                                <StepCard key={step}>
                                    <span>{String(index + 1).padStart(2, '0')}</span>
                                    <p>{step}</p>
                                </StepCard>
                            ))}
                        </StepGrid>
                    </MarketingInner>
                </MarketingSection>

                <MarketingSection>
                    <MarketingInner>
                        <SplitBlock>
                            <h2>Built for the Texas electricity market</h2>
                            <div>
                                <p>
                                    Texas has one of the most competitive residential electricity
                                    markets in the country, but more choice can also mean more
                                    complexity. TEG Residential helps homeowners understand the
                                    difference between energy charges, utility delivery charges,
                                    Electricity Facts Labels, contract terms, early termination
                                    fees, renewable content, and other factors that can affect the
                                    total monthly bill.
                                </p>
                                <p>
                                    Texas Energy Group brings a professional, customer-first
                                    approach to energy shopping. Whether you are moving into a new
                                    home, nearing the end of your current contract, or simply want
                                    to compare today’s options, our goal is to make residential
                                    electricity shopping more understandable, more efficient, and
                                    more transparent.
                                </p>
                            </div>
                        </SplitBlock>
                    </MarketingInner>
                </MarketingSection>

                <CtaBand>
                    <h2>Compare plans now</h2>
                    <p>
                        Residential electricity shopping, simplified. Compare residential
                        electricity plans with Texas Energy Group — built to help you find clear
                        options, understand your true cost, and enroll with confidence.
                    </p>
                    <CtaLink to="/">Start with your address</CtaLink>
                </CtaBand>
            </MarketingPage>
        </>
    )
}
